import { createSlice } from '@reduxjs/toolkit';
import { 
  fetchPostsWithPictures, 
  createPost, 
  loadMorePosts, 
  updatePost, 
  likePost, 
  unlikePost,
  fetchPostsBySentiment,
  bullPost,
  removeBullVote,
  bearPost,
  removeBearVote,
  fetchStocksForPost,
  updatePostStocks,
  fetchFollowingPostsThunk,

  // ✅ add the filtered thunks
  fetchPostsWithFilters,
  loadMorePostsWithFilters,

  fetchUserHistoryPosts,
  loadMoreUserHistoryPosts,
  fetchUserGroupHistoryPosts,
  loadMoreUserGroupHistoryPosts,
} from './postActions';

import { 
  createComment, 
  updateComment, 
  deleteComment, 
  likeComment, 
  unlikeComment 
} from '../comments/commentActions';

const initialState = {
  posts: [],
  followingPosts: [],
  followingHasMore: true,
  followingLoading: false,
  followingError: null,

  loading: false,
  error: null,

  createPostLoading: false,
  createPostError: null,
  createPostSuccess: false,

  updatePostLoading: false,
  updatePostError: null,
  updatePostSuccess: false,

  hasMore: true,
  loadingByType: false,
  errorByType: null,

  stockAssociations: {}, // Map of postId -> associated stocks
  loadingStocks: false,
  stocksError: null,

  userHistory: {
    posts: [],
    loading: false,
    error: null,
    hasMore: true,
    currentPage: 1,
    perPage: 10,
    sortBy: 'latest',
    period: 'all',
    total: null,
  },

  // GroupPost is a separate table from Post -- userHistory above can never
  // include a user's group posts, so this is its own parallel state slice.
  userGroupHistory: {
    posts: [],
    loading: false,
    error: null,
    hasMore: true,
    total: null,
  },


  // Filters
  currentFilters: {
    skip: 0,
    limit: 10,
    sort_by: 'latest',
    period: 'all',
    sentiment_filter: null,
    stock_symbol: null
  },
  isFiltered: false,
  filterLoading: false,
  filterError: null,

  // Meta
  totalCount: null,
  currentPage: 1,
  perPage: 10,
};

const normHasMore = (data, fallbackLen, pageSize = 10) =>
  (typeof data?.has_more === 'boolean')
    ? data.has_more
    : (typeof data?.hasMore === 'boolean')
      ? data.hasMore
      : (typeof fallbackLen === 'number' ? fallbackLen >= pageSize : true);

const getPostsArray = (payload) => payload?.posts ?? payload ?? [];

const postsSlice = createSlice({
  name: 'posts',
  initialState,
  reducers: {
    resetPostStatus: (state) => {
      state.createPostSuccess = false;
      state.updatePostSuccess = false;
      state.createPostError = null;
      state.updatePostError = null;
    },
    // ✅ put these INSIDE the slice
    setPostFilters: (state, action) => {
      const { sort_by, period, sentiment_filter = null, stock_symbol = null, limit } = action.payload || {};
      state.currentFilters = {
        ...state.currentFilters,
        ...(sort_by ? { sort_by } : {}),
        ...(period ? { period } : {}),
        sentiment_filter,
        stock_symbol,
        skip: 0,
        limit: limit ?? state.perPage ?? 10,
      };
      state.isFiltered = Boolean(
        (sort_by && sort_by !== 'latest') ||
        (period && period !== 'all') ||
        sentiment_filter ||
        stock_symbol
      );
    },
    resetPostsForNewQuery: (state) => {
      state.posts = [];
      state.hasMore = true;
      state.currentPage = 1;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ------- Base fetch -------
      .addCase(fetchPostsWithPictures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPostsWithPictures.fulfilled, (state, action) => {
        state.loading = false;
        const posts = getPostsArray(action.payload);
        state.posts = posts;
        state.hasMore = normHasMore(action.payload, posts.length, action.meta?.arg?.limit ?? 10);
        state.currentPage = action.payload?.current_page ?? 1;
        state.perPage = action.payload?.per_page ?? (action.meta?.arg?.limit ?? 10);
        state.isFiltered = false; // this is the unfiltered feed
        state.currentFilters = { ...initialState.currentFilters, skip: 0, limit: state.perPage };
      })
      .addCase(fetchPostsWithPictures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred while fetching posts';
      })

      // ------- Following feed -------
      .addCase(fetchFollowingPostsThunk.pending, (state) => {
        state.followingLoading = true;
        state.followingError = null;
      })
      .addCase(fetchFollowingPostsThunk.fulfilled, (state, action) => {
        state.followingLoading = false;
        const posts = getPostsArray(action.payload);
        state.followingPosts = posts;
        state.followingHasMore = normHasMore(action.payload, posts.length, 10);
      })
      .addCase(fetchFollowingPostsThunk.rejected, (state, action) => {
        state.followingLoading = false;
        state.followingError = action.payload || 'Failed to fetch following posts';
      })

      // ------- Create -------
      .addCase(createPost.pending, (state) => {
        state.createPostLoading = true;
        state.createPostSuccess = false;
        state.createPostError = null;
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.createPostLoading = false;
        state.createPostSuccess = true;

        const newPost = {
          ...action.payload,
          likes: action.payload.likes || 0,
          bull_count: action.payload.bull_count || 0,
          bear_count: action.payload.bear_count || 0,
          comments: action.payload.comments || [],
          is_liked: false,
          has_bull_vote: false,
          has_bear_vote: false,
          vote_consensus: action.payload.vote_consensus ?? null,
        };
        state.posts = [newPost, ...state.posts];

        if (action.payload.stock_ids?.length) {
          state.stockAssociations[action.payload.id] = action.payload.stock_ids.map(id => ({ id }));
        }
      })
      .addCase(createPost.rejected, (state, action) => {
        state.createPostLoading = false;
        state.createPostError = action.payload || 'Failed to create post';
      })

      // ------- Update -------
      .addCase(updatePost.pending, (state) => {
        state.updatePostLoading = true;
        state.updatePostSuccess = false;
        state.updatePostError = null;
      })
      .addCase(updatePost.fulfilled, (state, action) => {
        state.updatePostLoading = false;
        state.updatePostSuccess = true;

        const idx = state.posts.findIndex(p => p.id === action.payload.id);
        if (idx !== -1) {
          state.posts[idx] = {
            ...state.posts[idx],
            ...action.payload,
            comments: action.payload.comments ?? state.posts[idx].comments ?? [],
            likes: action.payload.likes ?? state.posts[idx].likes,
            bull_count: action.payload.bull_count ?? state.posts[idx].bull_count,
            bear_count: action.payload.bear_count ?? state.posts[idx].bear_count,
          };
        }
      })
      .addCase(updatePost.rejected, (state, action) => {
        state.updatePostLoading = false;
        state.updatePostError = action.payload || 'Failed to update post';
      })

      // ------- Load more (auto decides in thunk) -------
      .addCase(loadMorePosts.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadMorePosts.fulfilled, (state, action) => {
        state.loading = false;
        const newPosts = getPostsArray(action.payload);
        state.posts = [...state.posts, ...newPosts];

        // use top-level pagination fields from the new API
        state.hasMore = normHasMore(
          action.payload,
          newPosts.length,
          action.payload?.per_page ?? state.perPage
        );

        // track pagination if provided
        if (action.payload?.current_page) state.currentPage = action.payload.current_page;
        if (action.payload?.per_page) state.perPage = action.payload.per_page;
      })
      .addCase(loadMorePosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load more posts';
      })

      // ------- Filtered fetch -------
      .addCase(fetchPostsWithFilters.pending, (state) => {
        state.filterLoading = true;
        state.filterError = null;
      })
      .addCase(fetchPostsWithFilters.fulfilled, (state, action) => {
        state.filterLoading = false;
        const { data, filters, replace } = action.payload || {};
        const newPosts = getPostsArray(data);
        state.currentFilters = { ...state.currentFilters, ...filters, skip: 0 };
        state.isFiltered = Boolean(filters?.sentiment_filter || filters?.stock_symbol || filters?.sort_by !== 'latest' || filters?.period !== 'all');

        state.posts = replace === false ? [...state.posts, ...newPosts] : newPosts;
        state.hasMore = normHasMore(data, newPosts.length, filters?.limit ?? state.perPage);
        state.currentPage = data?.current_page ?? 1;
        state.perPage = data?.per_page ?? (filters?.limit ?? state.perPage);
      })
      .addCase(fetchPostsWithFilters.rejected, (state, action) => {
        state.filterLoading = false;
        state.filterError = action.payload || 'Failed to fetch posts with filters';
      })

      // ------- Load more (filtered) -------
      .addCase(loadMorePostsWithFilters.pending, (state) => {
        state.filterLoading = true;
      })
      .addCase(loadMorePostsWithFilters.fulfilled, (state, action) => {
        state.filterLoading = false;
        const { data, filters } = action.payload || {};
        const more = getPostsArray(data);
        state.posts = [...state.posts, ...more];
        state.hasMore = normHasMore(data, more.length, filters?.limit ?? state.perPage);
        state.currentFilters = { ...state.currentFilters, ...filters };
        state.currentPage = data?.current_page ?? state.currentPage;
        state.perPage = data?.per_page ?? state.perPage;
      })
      .addCase(loadMorePostsWithFilters.rejected, (state, action) => {
        state.filterLoading = false;
        state.filterError = action.payload || 'Failed to load more filtered posts';
      })

      // ------- Sentiment feed (path param) -------
      .addCase(fetchPostsBySentiment.pending, (state) => {
        state.loadingByType = true;
        state.errorByType = null;
      })
      .addCase(fetchPostsBySentiment.fulfilled, (state, action) => {
        state.loadingByType = false;
        const posts = getPostsArray(action.payload);
        state.posts = posts;
        state.hasMore = normHasMore(action.payload, posts.length, 10);
      })
      .addCase(fetchPostsBySentiment.rejected, (state, action) => {
        state.loadingByType = false;
        state.errorByType = action.payload || 'Failed to fetch posts by sentiment';
      })

      // ---------- LIKE ----------
      .addCase(likePost.pending, (state, action) => {
        const { postId } = action.meta.arg;
        const i = state.posts.findIndex(p => p.id === postId);
        if (i !== -1) {
          if (!state.posts[i].is_liked) state.posts[i].likes = (state.posts[i].likes || 0) + 1;
          state.posts[i].is_liked = true;
        }
        const fi = state.followingPosts.findIndex(p => p.id === postId);
        if (fi !== -1) {
          if (!state.followingPosts[fi].is_liked) state.followingPosts[fi].likes = (state.followingPosts[fi].likes || 0) + 1;
          state.followingPosts[fi].is_liked = true;
        }
      })
      .addCase(likePost.fulfilled, (state, action) => {
        const pid = action.payload.post_id;
        const serverCount = action.payload.like_count ?? action.payload.count;
        const apply = (p) => {
          if (!p) return;
          if (serverCount != null) p.likes = serverCount;
          p.is_liked = true;
        };
        apply(state.posts.find(p => p.id === pid));
        apply(state.followingPosts.find(p => p.id === pid));
      })
      .addCase(likePost.rejected, (state, action) => {
        const { postId } = action.meta.arg;
        const revert = (p) => {
          if (!p) return;
          if (p.is_liked) p.likes = Math.max(0, (p.likes || 0) - 1);
          p.is_liked = false;
        };
        revert(state.posts.find(p => p.id === postId));
        revert(state.followingPosts.find(p => p.id === postId));
      })

      // ---------- UNLIKE ----------
      .addCase(unlikePost.pending, (state, action) => {
        const { postId } = action.meta.arg;
        const apply = (p) => {
          if (!p) return;
          if (p.is_liked) p.likes = Math.max(0, (p.likes || 0) - 1);
          p.is_liked = false;
        };
        apply(state.posts.find(p => p.id === postId));
        apply(state.followingPosts.find(p => p.id === postId));
      })
      .addCase(unlikePost.fulfilled, (state, action) => {
        const pid = action.payload.post_id;
        const serverCount = action.payload.like_count ?? action.payload.count;
        const apply = (p) => {
          if (!p) return;
          if (serverCount != null) p.likes = serverCount;
          p.is_liked = false;
        };
        apply(state.posts.find(p => p.id === pid));
        apply(state.followingPosts.find(p => p.id === pid));
      })
      .addCase(unlikePost.rejected, (state, action) => {
        const { postId } = action.meta.arg;
        const revert = (p) => {
          if (!p) return;
          if (!p.is_liked) p.likes = (p.likes || 0) + 1;
          p.is_liked = true;
        };
        revert(state.posts.find(p => p.id === postId));
        revert(state.followingPosts.find(p => p.id === postId));
      })

      // ---------- BULL ----------
      .addCase(bullPost.pending, (state, action) => {
        const { postId } = action.meta.arg;
        const apply = (p) => {
          if (!p) return;
          if (p.has_bear_vote && p.bear_count > 0) p.bear_count -= 1;
          p.has_bear_vote = false;
          if (p.has_bull_vote) return;
          p.has_bull_vote = true;
          p.bull_count = (p.bull_count || 0) + 1;
        };
        apply(state.posts.find(p => p.id === postId));
        apply(state.followingPosts.find(p => p.id === postId));
      })
      .addCase(bullPost.fulfilled, (state, action) => {
        const {
          post_id,
          bull_count,
          bear_count,
          has_bull_vote,
          has_bear_vote,
          vote_consensus,
        } = action.payload;

        const setFromServer = (p) => {
          if (!p) return;
          if (bull_count != null) p.bull_count = bull_count;
          if (bear_count != null) p.bear_count = bear_count;
          if (typeof has_bull_vote === 'boolean') p.has_bull_vote = has_bull_vote;
          if (typeof has_bear_vote === 'boolean') p.has_bear_vote = has_bear_vote;
          if (vote_consensus !== undefined) p.vote_consensus = vote_consensus;
        };

        setFromServer(state.posts.find((p) => p.id === post_id));
        setFromServer(state.followingPosts.find((p) => p.id === post_id));
      })
      .addCase(bullPost.rejected, (state, action) => {
        const { postId } = action.meta.arg;
        const revert = (p) => {
          if (!p) return;
          if (p.has_bull_vote) {
            p.has_bull_vote = false;
            if ((p.bull_count || 0) > 0) p.bull_count -= 1;
          }
        };
        revert(state.posts.find(p => p.id === postId));
        revert(state.followingPosts.find(p => p.id === postId));
      })

      // ---------- BEAR ----------
      .addCase(bearPost.pending, (state, action) => {
        const { postId } = action.meta.arg;
        const apply = (p) => {
          if (!p) return;
          if (p.has_bull_vote && p.bull_count > 0) p.bull_count -= 1;
          p.has_bull_vote = false;
          if (p.has_bear_vote) return;
          p.has_bear_vote = true;
          p.bear_count = (p.bear_count || 0) + 1;
        };
        apply(state.posts.find(p => p.id === postId));
        apply(state.followingPosts.find(p => p.id === postId));
      })
      .addCase(bearPost.fulfilled, (state, action) => {
        const {
          post_id,
          bear_count,
          bull_count,
          has_bear_vote,
          has_bull_vote,
          vote_consensus,
        } = action.payload;

        const setFromServer = (p) => {
          if (!p) return;
          if (bear_count != null) p.bear_count = bear_count;
          if (bull_count != null) p.bull_count = bull_count;
          if (typeof has_bear_vote === 'boolean') p.has_bear_vote = has_bear_vote;
          if (typeof has_bull_vote === 'boolean') p.has_bull_vote = has_bull_vote;
          if (vote_consensus !== undefined) p.vote_consensus = vote_consensus;
        };

        setFromServer(state.posts.find((p) => p.id === post_id));
        setFromServer(state.followingPosts.find((p) => p.id === post_id));
      })

      .addCase(bearPost.rejected, (state, action) => {
        const { postId } = action.meta.arg;
        const revert = (p) => {
          if (!p) return;
          if (p.has_bear_vote) {
            p.has_bear_vote = false;
            if ((p.bear_count || 0) > 0) p.bear_count -= 1;
          }
        };
        revert(state.posts.find(p => p.id === postId));
        revert(state.followingPosts.find(p => p.id === postId));
      })

      // ---------- REMOVE BULL ----------
      .addCase(removeBullVote.pending, (state, action) => {
        const { postId } = action.meta.arg;
        const apply = (p) => {
          if (!p) return;
          if (p.has_bull_vote) {
            p.has_bull_vote = false;
            if ((p.bull_count || 0) > 0) p.bull_count -= 1;
          }
        };
        apply(state.posts.find(p => p.id === postId));
        apply(state.followingPosts.find(p => p.id === postId));
      })
      .addCase(removeBullVote.fulfilled, (state, action) => {
        const {
          post_id,
          bull_count,
          bear_count,
          has_bull_vote,
          has_bear_vote,
          vote_consensus,
        } = action.payload;

        const setFromServer = (p) => {
          if (!p) return;
          if (bull_count != null) p.bull_count = bull_count;
          if (bear_count != null) p.bear_count = bear_count;
          if (typeof has_bull_vote === 'boolean') p.has_bull_vote = has_bull_vote;
          if (typeof has_bear_vote === 'boolean') p.has_bear_vote = has_bear_vote;
          if (vote_consensus !== undefined) p.vote_consensus = vote_consensus;
        };

        setFromServer(state.posts.find((p) => p.id === post_id));
        setFromServer(state.followingPosts.find((p) => p.id === post_id));
      })

      .addCase(removeBullVote.rejected, (state, action) => {
        const { postId } = action.meta.arg;
        const revert = (p) => {
          if (!p) return;
          if (!p.has_bull_vote) {
            p.has_bull_vote = true;
            p.bull_count = (p.bull_count || 0) + 1;
          }
        };
        revert(state.posts.find(p => p.id === postId));
        revert(state.followingPosts.find(p => p.id === postId));
      })

      // ---------- REMOVE BEAR ----------
      .addCase(removeBearVote.pending, (state, action) => {
        const { postId } = action.meta.arg;
        const apply = (p) => {
          if (!p) return;
          if (p.has_bear_vote) {
            p.has_bear_vote = false;
            if ((p.bear_count || 0) > 0) p.bear_count -= 1;
          }
        };
        apply(state.posts.find(p => p.id === postId));
        apply(state.followingPosts.find(p => p.id === postId));
      })
      .addCase(removeBearVote.fulfilled, (state, action) => {
        const {
          post_id,
          bear_count,
          bull_count,
          has_bear_vote,
          has_bull_vote,
          vote_consensus,
        } = action.payload;

        const setFromServer = (p) => {
          if (!p) return;
          if (bear_count != null) p.bear_count = bear_count;
          if (bull_count != null) p.bull_count = bull_count;
          if (typeof has_bear_vote === 'boolean') p.has_bear_vote = has_bear_vote;
          if (typeof has_bull_vote === 'boolean') p.has_bull_vote = has_bull_vote;
          if (vote_consensus !== undefined) p.vote_consensus = vote_consensus;
        };

        setFromServer(state.posts.find((p) => p.id === post_id));
        setFromServer(state.followingPosts.find((p) => p.id === post_id));
      })

      .addCase(removeBearVote.rejected, (state, action) => {
        const { postId } = action.meta.arg;
        const revert = (p) => {
          if (!p) return;
          if (!p.has_bear_vote) {
            p.has_bear_vote = true;
            p.bear_count = (p.bear_count || 0) + 1;
          }
        };
        revert(state.posts.find(p => p.id === postId));
        revert(state.followingPosts.find(p => p.id === postId));
      })

      // ---------- Comments ----------
      .addCase(createComment.fulfilled, (state, action) => {
        const { post_id, parent_comment_id } = action.payload;
        const pi = state.posts.findIndex(p => p.id === post_id);
        if (pi === -1) return;

        state.posts[pi].comments ||= [];
        if (parent_comment_id) {
          const ci = state.posts[pi].comments.findIndex(c => c.id === parent_comment_id);
          if (ci !== -1) {
            state.posts[pi].comments[ci].replies ||= [];
            state.posts[pi].comments[ci].replies.push(action.payload);
          }
        } else {
          state.posts[pi].comments.push(action.payload);
        }
      })
      .addCase(likeComment.fulfilled, (state, action) => {
        const { id, post_id } = action.payload;
        const pi = state.posts.findIndex(p => p.id === post_id);
        if (pi === -1 || !state.posts[pi].comments) return;

        const inc = (arr) => {
          for (const c of arr) {
            if (c.id === id) { c.is_liked = true; c.like_count = (c.like_count || 0) + 1; return true; }
            if (c.replies && inc(c.replies)) return true;
          }
          return false;
        };
        inc(state.posts[pi].comments);
      })
      .addCase(unlikeComment.fulfilled, (state, action) => {
        const { id, post_id } = action.payload;
        const pi = state.posts.findIndex(p => p.id === post_id);
        if (pi === -1 || !state.posts[pi].comments) return;

        const dec = (arr) => {
          for (const c of arr) {
            if (c.id === id) { c.is_liked = false; c.like_count = Math.max(0, (c.like_count || 1) - 1); return true; }
            if (c.replies && dec(c.replies)) return true;
          }
          return false;
        };
        dec(state.posts[pi].comments);
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        const { id, post_id } = action.payload;
        const pi = state.posts.findIndex(p => p.id === post_id);
        if (pi === -1 || !state.posts[pi].comments) return;

        const upd = (arr) => {
          for (let i = 0; i < arr.length; i++) {
            if (arr[i].id === id) { arr[i] = { ...arr[i], ...action.payload }; return true; }
            if (arr[i].replies && upd(arr[i].replies)) return true;
          }
          return false;
        };
        upd(state.posts[pi].comments);
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { id, post_id } = action.payload;
        const pi = state.posts.findIndex(p => p.id === post_id);
        if (pi === -1 || !state.posts[pi].comments) return;

        // remove top-level
        state.posts[pi].comments = state.posts[pi].comments.filter(c => c.id !== id);
        // remove nested
        for (const c of state.posts[pi].comments) {
          if (c.replies) c.replies = c.replies.filter(r => r.id !== id);
        }
      })

      // ---------- Stocks ----------
      .addCase(fetchStocksForPost.pending, (state) => {
        state.loadingStocks = true;
        state.stocksError = null;
      })
      .addCase(fetchStocksForPost.fulfilled, (state, action) => {
        state.loadingStocks = false;
        const postId = action.meta.arg.postId;
        state.stockAssociations[postId] = action.payload;
      })
      .addCase(fetchStocksForPost.rejected, (state, action) => {
        state.loadingStocks = false;
        state.stocksError = action.payload;
      })
      .addCase(updatePostStocks.pending, (state) => {
        state.loadingStocks = true;
        state.stocksError = null;
      })
      .addCase(updatePostStocks.fulfilled, (state, action) => {
        state.loadingStocks = false;
        const { postId, stockIds } = action.payload;
        if (state.stockAssociations[postId]) {
          state.stockAssociations[postId] = stockIds.map(id => ({ id }));
        }
      })
      .addCase(updatePostStocks.rejected, (state, action) => {
        state.loadingStocks = false;
        state.stocksError = action.payload;
      })
      .addCase(fetchUserHistoryPosts.pending, (state) => {
        state.userHistory.loading = true;
        state.userHistory.error = null;
      })
      .addCase(fetchUserHistoryPosts.fulfilled, (state, action) => {
        state.userHistory.loading = false;

        const payload = action.payload || {};
        const arr = payload.posts ?? payload ?? [];

        state.userHistory.posts = arr;
        state.userHistory.hasMore =
          typeof payload.has_more === 'boolean'
            ? payload.has_more
            : (Array.isArray(arr) ? arr.length >= (payload.per_page ?? state.userHistory.perPage) : true);

        state.userHistory.currentPage = payload.current_page ?? 1;
        state.userHistory.perPage = payload.per_page ?? state.userHistory.perPage;
        state.userHistory.sortBy = payload.sort_by ?? state.userHistory.sortBy;
        state.userHistory.period = payload.period ?? state.userHistory.period;
        state.userHistory.total = payload.total ?? state.userHistory.total;
      })
      .addCase(fetchUserHistoryPosts.rejected, (state, action) => {
        state.userHistory.loading = false;
        state.userHistory.error = action.payload || 'Failed to fetch user history posts';
      })

      .addCase(loadMoreUserHistoryPosts.pending, (state) => {
        state.userHistory.loading = true;
      })
      .addCase(loadMoreUserHistoryPosts.fulfilled, (state, action) => {
        state.userHistory.loading = false;

        const payload = action.payload || {};
        const more = payload.posts ?? payload ?? [];

        state.userHistory.posts = [...state.userHistory.posts, ...more];

        state.userHistory.hasMore =
          typeof payload.has_more === 'boolean'
            ? payload.has_more
            : (Array.isArray(more) ? more.length >= (payload.per_page ?? state.userHistory.perPage) : true);

        state.userHistory.currentPage = payload.current_page ?? state.userHistory.currentPage;
        state.userHistory.perPage = payload.per_page ?? state.userHistory.perPage;
        state.userHistory.sortBy = payload.sort_by ?? state.userHistory.sortBy;
        state.userHistory.period = payload.period ?? state.userHistory.period;
        state.userHistory.total = payload.total ?? state.userHistory.total;
      })
      .addCase(loadMoreUserHistoryPosts.rejected, (state, action) => {
        state.userHistory.loading = false;
        state.userHistory.error = action.payload || 'Failed to load more user history posts';
      })

      .addCase(fetchUserGroupHistoryPosts.pending, (state) => {
        state.userGroupHistory.loading = true;
        state.userGroupHistory.error = null;
      })
      .addCase(fetchUserGroupHistoryPosts.fulfilled, (state, action) => {
        const payload = action.payload || {};
        state.userGroupHistory.loading = false;
        state.userGroupHistory.posts = payload.posts ?? [];
        state.userGroupHistory.hasMore = payload.has_more ?? false;
        state.userGroupHistory.total = payload.total ?? null;
      })
      .addCase(fetchUserGroupHistoryPosts.rejected, (state, action) => {
        state.userGroupHistory.loading = false;
        state.userGroupHistory.error = action.payload || 'Failed to fetch user group history posts';
      })

      .addCase(loadMoreUserGroupHistoryPosts.pending, (state) => {
        state.userGroupHistory.loading = true;
      })
      .addCase(loadMoreUserGroupHistoryPosts.fulfilled, (state, action) => {
        const payload = action.payload || {};
        state.userGroupHistory.loading = false;
        state.userGroupHistory.posts = [...state.userGroupHistory.posts, ...(payload.posts ?? [])];
        state.userGroupHistory.hasMore = payload.has_more ?? false;
        state.userGroupHistory.total = payload.total ?? state.userGroupHistory.total;
      })
      .addCase(loadMoreUserGroupHistoryPosts.rejected, (state, action) => {
        state.userGroupHistory.loading = false;
        state.userGroupHistory.error = action.payload || 'Failed to load more user group history posts';
      });
  }
});

export const { resetPostStatus, setPostFilters, resetPostsForNewQuery } = postsSlice.actions;
export default postsSlice.reducer;

